import { useState } from 'react'
import { stationsAPI } from '../services/api'
import ConnectorCard from './ConnectorCard'
import {
  allocatedPowerWatts,
  cabinetPowerLabel,
  chargerImageSrc,
  chargingConnectorCount,
  isLoadBalancingEnabled,
} from '../utils/chargingMetrics'
import './StationMachine.css'

const GUN_HOTSPOTS = [
  { id: 1, className: 'gun-hotspot--left', label: '#1' },
  { id: 2, className: 'gun-hotspot--right', label: '#2' },
]

function StationMachine({
  station,
  connectors = [],
  isStationConnected = true,
  isAdmin = false,
  onUpdate,
  onStationChange,
}) {
  const [busyId, setBusyId] = useState(null)
  const [savingDlb, setSavingDlb] = useState(false)
  const left = connectors[0]
  const right = connectors[1]
  const extra = connectors.slice(2)
  const idTag = station?.simulation?.defaultIdTag || 'USER001'
  const loadBalancing = isLoadBalancingEnabled(station)
  const powerLabel = cabinetPowerLabel(station, connectors)
  const imageSrc = chargerImageSrc(station)
  const chargingCount = chargingConnectorCount(connectors)

  const connectorById = (id) => connectors.find((c) => c.id === id)

  const handleStartCharging = async (connectorId) => {
    setBusyId(connectorId)
    try {
      await stationsAPI.startCharging(station.stationId, connectorId, idTag)
      onUpdate?.()
      setTimeout(() => onUpdate?.(), 500)
    } catch (err) {
      alert(`Failed to start charging: ${err.response?.data?.error || err.message}`)
    } finally {
      setBusyId(null)
    }
  }

  const handleStopCharging = async (connectorId) => {
    setBusyId(connectorId)
    try {
      await stationsAPI.stopCharging(station.stationId, connectorId, 'Local')
      onUpdate?.()
      setTimeout(() => onUpdate?.(), 500)
    } catch (err) {
      alert(`Failed to stop charging: ${err.response?.data?.error || err.message}`)
    } finally {
      setBusyId(null)
    }
  }

  const handleGunDoubleClick = (gunId, event) => {
    event.preventDefault()
    event.stopPropagation()
    const connector = connectorById(gunId)
    if (!connector || !isStationConnected || busyId === gunId) return

    if (connector.state === 'Charging') {
      handleStopCharging(gunId)
      return
    }
    if (connector.state === 'Available') {
      handleStartCharging(gunId)
    }
  }

  const handleLoadBalancingChange = async (event) => {
    event.stopPropagation()
    const enabled = event.target.checked
    const previous = loadBalancing
    onStationChange?.({
      ...station,
      simulation: { ...station.simulation, loadBalancing: enabled },
    })
    setSavingDlb(true)
    try {
      const response = await stationsAPI.patchSimulation(station.stationId, {
        loadBalancing: enabled,
      })
      if (response?.data) {
        onStationChange?.(response.data)
      }
    } catch (err) {
      onStationChange?.({
        ...station,
        simulation: { ...station.simulation, loadBalancing: previous },
      })
      alert(`Failed to update load balancing: ${err.response?.data?.error || err.message}`)
    } finally {
      setSavingDlb(false)
    }
  }

  const gunTitle = (gunId) => {
    const connector = connectorById(gunId)
    if (!isStationConnected) return 'Start the station first'
    if (!connector) return `Gun ${gunId} not configured`
    if (connector.state === 'Charging') return `Double-click gun ${gunId} to stop`
    if (connector.state === 'Available') return `Double-click gun ${gunId} to start`
    return `Gun ${gunId} is ${connector.state}`
  }

  const renderCard = (connector) => (
    <ConnectorCard
      stationId={station.stationId}
      connector={connector}
      allocatedWatts={allocatedPowerWatts(station, connectors, connector)}
      isStationConnected={isStationConnected}
      defaultIdTag={idTag}
      onUpdate={onUpdate}
    />
  )

  return (
    <div className="station-machine">
      <div className="station-machine__row">
        <div className="station-machine__bay station-machine__bay--left">
          {left && renderCard(left)}
        </div>

        <div className="station-machine__stage-wrap">
          <div className="machine-stage">
            {GUN_HOTSPOTS.map((hotspot) => {
              const connector = connectorById(hotspot.id)
              if (connector?.state !== 'Charging') return null
              return (
                <span
                  key={`glow-${hotspot.id}`}
                  className={`gun-glow ${hotspot.className}`}
                  aria-hidden="true"
                />
              )
            })}
            <img
              className="machine-stage__image"
              src={imageSrc}
              alt={`${station.vendor || ''} ${station.model || 'charger'}`.trim()}
              draggable="false"
            />
            <div
              className={`machine-kw ${chargingCount >= 2 && loadBalancing ? 'machine-kw--split' : ''}`}
              aria-label={`Power ${powerLabel}`}
            >
              {powerLabel}
            </div>
            {GUN_HOTSPOTS.map((hotspot) => {
              const connector = connectorById(hotspot.id)
              const charging = connector?.state === 'Charging'
              return (
                <button
                  key={hotspot.id}
                  type="button"
                  className={`gun-hotspot ${hotspot.className} ${charging ? 'is-charging' : ''}`}
                  disabled={!isStationConnected || !connector || busyId === hotspot.id}
                  title={gunTitle(hotspot.id)}
                  aria-label={gunTitle(hotspot.id)}
                  onDoubleClick={(event) => handleGunDoubleClick(hotspot.id, event)}
                  onClick={(event) => event.stopPropagation()}
                >
                  <span className="gun-hotspot__label">{hotspot.label}</span>
                </button>
              )
            })}
          </div>
          <label className="machine-dlb checkbox-label" onClick={(event) => event.stopPropagation()}>
            <input
              type="checkbox"
              checked={loadBalancing}
              disabled={!isAdmin || savingDlb}
              onChange={handleLoadBalancingChange}
            />
            Dynamic load balancing
          </label>
        </div>

        <div className="station-machine__bay station-machine__bay--right">
          {right && renderCard(right)}
        </div>
      </div>

      {extra.length > 0 && (
        <div className="station-machine__extra">
          {extra.map((connector) => (
            <div key={connector.id}>{renderCard(connector)}</div>
          ))}
        </div>
      )}
    </div>
  )
}

export default StationMachine
